import React, { useState } from 'react';
import { Card, Icon } from 'tea-component';
import { IValidationReport, IValidationResult } from '../../types';
import './index.scss';

interface IValidationPanelProps {
  report: IValidationReport;
}

/**
 * 单个验证项组件
 * 必须是独立组件才能使用 hooks
 */
interface IValidationItemProps {
  result: IValidationResult;
}

const ValidationItem: React.FC<IValidationItemProps> = ({ result }) => {
  const [showDetails, setShowDetails] = useState(false);

  return (
    <div className="dba-validation-item">
      <div className="dba-validation-item-main">
        <span className="dba-validation-item-message">{result.message}</span>
        {result.field && <span className="dba-validation-item-field">[{result.field}]</span>}
        {result.position && (
          <span className="dba-validation-item-position">
            Line {result.position.line}, Column {result.position.column}
          </span>
        )}
      </div>
      {result.details && (
        <div className="dba-validation-item-details">
          <button
            className="dba-details-toggle"
            onClick={() => setShowDetails(!showDetails)}
          >
            {showDetails ? '隐藏详情' : '查看详情'}
          </button>
          {showDetails && (
            <pre className="dba-details-content">{result.details}</pre>
          )}
        </div>
      )}
    </div>
  );
};

const ValidationPanel: React.FC<IValidationPanelProps> = React.memo(({ report }) => {
  const [expandedSections, setExpandedSections] = useState<{
    errors: boolean;
    warnings: boolean;
    successes: boolean;
  }>({
    errors: true,
    warnings: true,
    successes: false,
  });

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const renderStatus = () => {
    if (report.errors.length > 0) {
      return (
        <div className="dba-validation-status dba-validation-status--error">
          <Icon type="error" />
          <span>检测失败 - 发现 {report.errors.length} 个错误</span>
        </div>
      );
    }

    if (report.warnings.length > 0) {
      return (
        <div className="dba-validation-status dba-validation-status--warning">
          <Icon type="warning" />
          <span>检测通过（有警告） - {report.warnings.length} 个警告</span>
        </div>
      );
    }

    return (
      <div className="dba-validation-status dba-validation-status--success">
        <Icon type="success" />
        <span>✓ 数据格式验证通过</span>
      </div>
    );
  };

  return (
    <Card className="dba-validation-panel">
      <Card.Header>
        <h3>格式检测结果</h3>
      </Card.Header>
      <Card.Body>
        {renderStatus()}

        {/* 错误列表 */}
        {report.errors.length > 0 && (
          <div className="dba-validation-section">
            <div
              className="dba-validation-section-header"
              onClick={() => toggleSection('errors')}
            >
              <Icon type={expandedSections.errors ? 'arrowdown' : 'arrowright'} />
              <span className="dba-validation-section-title dba-validation-section-title--error">
                错误 ({report.errors.length})
              </span>
            </div>
            {expandedSections.errors && (
              <div className="dba-validation-section-content">
                {report.errors.map((error, index) => (
                  <ValidationItem key={index} result={error} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* 警告列表 */}
        {report.warnings.length > 0 && (
          <div className="dba-validation-section">
            <div
              className="dba-validation-section-header"
              onClick={() => toggleSection('warnings')}
            >
              <Icon type={expandedSections.warnings ? 'arrowdown' : 'arrowright'} />
              <span className="dba-validation-section-title dba-validation-section-title--warning">
                警告 ({report.warnings.length})
              </span>
            </div>
            {expandedSections.warnings && (
              <div className="dba-validation-section-content">
                {report.warnings.map((warning, index) => (
                  <ValidationItem key={index} result={warning} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* 成功项列表 */}
        {report.successes.length > 0 && (
          <div className="dba-validation-section">
            <div
              className="dba-validation-section-header"
              onClick={() => toggleSection('successes')}
            >
              <Icon type={expandedSections.successes ? 'arrowdown' : 'arrowright'} />
              <span className="dba-validation-section-title dba-validation-section-title--success">
                检测项 ({report.successes.length})
              </span>
            </div>
            {expandedSections.successes && (
              <div className="dba-validation-section-content">
                {report.successes.map((success, index) => (
                  <ValidationItem key={index} result={success} />
                ))}
              </div>
            )}
          </div>
        )}
      </Card.Body>
    </Card>
  );
});

ValidationPanel.displayName = 'ValidationPanel';

export default ValidationPanel;

